import React, { Component } from 'react';
import { injectIntl } from 'react-intl';
import * as d3 from 'd3';
import glamorous from 'glamorous';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';

import { createTooltip } from '../helpers';

const MARGIN = { top: 10, bottom: 10, left: 10, right: 10 };
const LEGEND = { itemSize: 20, letterSpacing: 5 };

const flatten = (segments) => {
  const items = [];

  const recurse = (label, item, index) => {
    if (!isEmpty(item.subItems)) {
      item.subItems.forEach(subItem => {
        recurse(item.label, subItem, index);
      });
    } else {
      items.push({
        index,
        parentLabel: label,
        label: item.label,
        recallCount: item.recallCount,
        code: item.code,
        value: item.value,
        description: item.description,
        link: item.link,
        uri: item.uri,
      });
    }
  };
  segments.forEach((item, index) => {
    recurse(null, item, index);
  });
  return items;
};

const buildColors = () => d3.scaleOrdinal(d3.schemeCategory20c);

const buildChart = ({ segments, color, chartRef, isFullScreen, showVisualData, intl, goToSearchPage }) => {
  const sectionColors = buildColors();

  const element = chartRef;
  const width = element.offsetWidth - MARGIN.left - MARGIN.right;
  const height = element.offsetHeight - MARGIN.top - MARGIN.bottom;
  const minRadiusForLabel = height / 50;
  element.innerHTML = '';

  const svg = d3
    .select(element)
    .append('svg')
    .attr('width', element.offsetWidth)
    .attr('height', element.offsetHeight)
    .attr('class', 'bubble');

  const bubble = d3
    .pack()
    .size([width, height])
    .padding(1.5);

  const flattenedSegments = flatten(segments);

  const root = d3
    .hierarchy({ children: flattenedSegments })
    .sum((segment) => (showVisualData ? segment.recallCount : height / 30))
    .sort((aSegment, bSegment) => bSegment.recallCount - aSegment.recallCount);

  // Calculate median (on thumb version we only show label for items with a value > median)
  const recallCounts = flattenedSegments.map((segment) => segment.recallCount);
  let median;
  if (!recallCounts.length) {
    median = 0;
  }
  recallCounts.sort((a, b) => a - b);
  const half = Math.floor(recallCounts.length / 2);
  median = recallCounts.length % 2 ? recallCounts[half] : (recallCounts[half - 1] + recallCounts[half]) / 2;

  bubble(root);

  const tip = createTooltip({ intl });
  svg.call(tip);

  const chart = svg
    .selectAll('.node')
    .data(root.children)
    .enter()
    .append('g')
    .attr('class', 'node')
    .attr('transform', (node) => `translate(${node.x}, ${node.y})`);

  chart
    .append('circle')
    .attr('r', (node) => (showVisualData ? node.r : 20))
    .style('cursor', 'pointer')
    .style('fill', (node) => (color || sectionColors(node.data.index)))
    .on('mouseover', tip.show)
    .on('mousemove', tip.move)
    .on('mouseout', tip.hide)
    .on('click', (node) => {
      tip.hide();
      goToSearchPage(get(node, 'data.link.params'), intl);
    });

  const text = chart
    .append('text')
    .attr('dy', '.3em')
    .style('text-anchor', 'middle')
    .style('font-size', '1rem')
    .style('cursor', 'pointer')
    .on('mouseover', tip.show)
    .on('mousemove', tip.move)
    .on('mouseout', tip.hide)
    .on('click', (node) => {
      tip.hide();
      goToSearchPage(get(node, 'data.link.params'), intl);
    });

  text
    .filter((node) => node.r >= minRadiusForLabel)
    .text((node) => {
      if (isFullScreen || node.value >= median) {
        if (node.r < minRadiusForLabel) {
          return '';
        }
        if (node.data.label.length > node.r / 3) {
          return `${node.data.label.substring(0, node.r / 3)}...`;
        }
        return node.data.label;
      }
      return '';
    });

  // Build the legend
  if (isFullScreen) {
    const legend = svg
      .selectAll('.legend')
      .data(segments)
      .enter()
      .append('g')
      .attr('class', 'legend')
      .attr('transform', (segment, idx) => {
        const x = MARGIN.left;
        const y = idx * (LEGEND.itemSize + LEGEND.letterSpacing) + MARGIN.top;
        return `translate(${x},${y})`;
      });

    legend
      .append('rect')
      .attr('width', LEGEND.itemSize)
      .attr('height', LEGEND.itemSize)
      .style('fill', (segment, idx) => (color || sectionColors(idx)))
      .style('stroke', (segment, idx) => (color || sectionColors(idx)));

    legend
      .append('text')
      .attr('x', LEGEND.itemSize + LEGEND.letterSpacing)
      .attr('y', LEGEND.itemSize - LEGEND.itemSize / 4)
      .text((segment) => segment.label);
  }
};

class WordCloud extends Component {
  constructor(props) {
    super(props);

    const { dataset: { data: segments }, isFullScreen, showVisualData = true, intl, goToSearchPage } = props;
    this.segments = segments;
    this.isFullScreen = isFullScreen;
    this.showVisualData = showVisualData;
    this.goToSearchPage = goToSearchPage;
    this.intl = intl;

    this.chartRef = null;
  }

  componentDidMount() {
    buildChart(this);
  }

  render() {
    const { isFullScreen } = this.props;
    return (
      <glamorous.Div
        innerRef={(ref) => { this.chartRef = ref; }}
        className="d3-chart"
        css={{
          display: 'flex',
          height: isFullScreen ? '100%' : 400,
          width: '100% !important',
        }}
      />
    );
  }
}
export default injectIntl(WordCloud);



// WEBPACK FOOTER //
// ./src/views/dashboard/chart/word-cloud/index.js